import { FileDownloadOutlined } from '@mui/icons-material';
import { Button, ButtonSize, ButtonVariant } from 'finara-components';
import React from 'react';
import toast from 'react-hot-toast';

interface CsvExportButtonProps<T> {
  /**
   * The data to export to CSV
   */
  data: T[];
  /**
   * The filename for the exported CSV (without .csv extension)
   */
  filename: string;
  /**
   * Function to convert each data item to a CSV row object
   * Returns an object where keys are column headers and values are the cell values
   */
  columns: (item: T) => Record<string, string | number | boolean | null | undefined>;
  /**
   * Button variant (default: 'white')
   */
  variant?: ButtonVariant;
  /**
   * Button size (default: 'small')
   */
  size?: ButtonSize;
  /**
   * Button text (default: 'Exportar CSV')
   */
  buttonText?: string;
  /**
   * Additional CSS classes for the button
   */
  className?: string;
  /**
   * Whether to show the icon (default: true)
   */
  showIcon?: boolean;
  /**
   * Optional callback when export starts
   */
  onExportStart?: () => void;
  /**
   * Optional callback when export completes successfully
   */
  onExportComplete?: () => void;
  /**
   * Optional callback when export fails
   */
  onExportError?: (error: Error) => void;
}

/**
 * CsvExportButton - A reusable component for exporting table data to CSV format
 *
 * @example
 * ```tsx
 * <CsvExportButton
 *   data={employees}
 *   filename="empleados"
 *   columns={(employee) => ({
 *     'Nombre': employee.name,
 *     'Email': employee.email,
 *     'Departamento': employee.department,
 *     'Salario': employee.salary
 *   })}
 * />
 * ```
 */
export function CsvExportButton<T>({
  data,
  filename,
  columns,
  variant = 'white',
  size = 'small',
  buttonText = 'Exportar CSV',
  className,
  showIcon = true,
  onExportStart,
  onExportComplete,
  onExportError,
}: CsvExportButtonProps<T>): React.ReactElement {
  /**
   * Escapes CSV values to handle commas, quotes, and newlines
   */
  const escapeCsvValue = (value: string | number | boolean | null | undefined): string => {
    if (value === null || value === undefined) {
      return '';
    }

    const stringValue = String(value);

    // If the value contains comma, quote, or newline, wrap it in quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      // Escape existing quotes by doubling them
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  };

  /**
   * Converts data array to CSV string
   */
  const convertToCSV = (items: T[]): string => {
    if (items.length === 0) {
      return '';
    }

    // Get headers from the first item
    const firstRow = columns(items[0] as T);
    const headers = Object.keys(firstRow);

    // Create CSV header row
    const headerRow = headers.map(escapeCsvValue).join(',');

    // Create CSV data rows
    const dataRows = items.map(item => {
      const row = columns(item);
      return headers.map(header => escapeCsvValue(row[header])).join(',');
    });

    // Combine header and data rows
    return [headerRow, ...dataRows].join('\n');
  };

  /**
   * Triggers the CSV download
   */
  const downloadCSV = (csvContent: string, fileName: string) => {
    // Add BOM for proper UTF-8 encoding in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    // Create a link to the file
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Handles the export button click
   */
  const handleExport = () => {
    try {
      // Call onExportStart callback if provided
      onExportStart?.();

      // Validate data
      // eslint-disable-next-line
      if (!data || data.length === 0) {
        toast.error('No hay datos para exportar');
        return;
      }

      // Convert to CSV
      const csvContent = convertToCSV(data);

      if (!csvContent) {
        toast.error('Error al generar el archivo CSV');
        return;
      }

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const fullFilename = `${filename}_${timestamp}`;

      // Download the file
      downloadCSV(csvContent, fullFilename);

      // Call onExportComplete callback if provided
      onExportComplete?.();
    } catch (error) {
      console.error('Error al exportar CSV:', error);
      toast.error('Error al exportar el archivo CSV');

      // Call onExportError callback if provided
      if (onExportError && error instanceof Error) {
        onExportError(error);
      }
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      startIcon={showIcon ? <FileDownloadOutlined /> : undefined}
      onClick={handleExport}
      className={className}
      // eslint-disable-next-line
      disabled={!data || data.length === 0}
    >
      {buttonText}
    </Button>
  );
}
